import * as fs from 'fs';
import * as path from 'path';
import { ResourceLoader } from '../core/resourceLoader.js';
import { createLogger } from '../core/index.js';

const logger = createLogger('QueryTroubleshootingCases');

/**
 * 故障排除案例元数据
 */
export interface TroubleshootingCase {
    id: string;              // 案例ID（文件名）
    framework: string;       // 框架（flutter, vue3, react）
    title: string;           // 标题
    problemType: string;     // 问题类型
    tags: string[];          // 标签（用于匹配）
    severity: string;        // 严重程度
    timeSaved: string;       // 节省时间估计
    filePath: string;        // 文件路径
}

/**
 * 查询结果
 */
export interface TroubleshootingQueryResult {
    cases: Array<{
        id: string;
        title: string;
        framework: string;
        problemType: string;
        tags: string[];
        matchScore: number;  // 匹配分数 0-100
        timeSaved: string;
        preview: string;     // 预览摘要
    }>;
    totalFound: number;
    queryInfo: {
        framework?: string;
        keywords: string[];
        errorMessage?: string;
    };
}

const KEYWORD_ALIAS_MAP: Record<string, string[]> = {
    svg: ['矢量', 'svgpicture', 'svgpicture.asset'],
    color: ['颜色', '色值', '发灰', '发浅', '变淡'],
    colorfilter: ['color filter', '二次染色', '染色'],
    opacity: ['透明度', 'fill-opacity', 'alpha'],
    viewbox: ['view box', '视口'],
    icon: ['图标', 'badge', 'flag'],
    chevron: ['arrow', '箭头'],
    bitmap: ['位图', 'png', 'image.asset'],
    layout: ['布局', 'spacing', '间距', '偏移'],
    sketch: ['设计稿', '还原', 'measure', 'restoration'],
};

function normalizeKeyword(value: string): string {
    return value.trim().toLowerCase();
}

function expandKeywords(keywords: string[]): string[] {
    const expanded = new Set<string>();

    for (const keyword of keywords) {
        const normalized = normalizeKeyword(keyword);
        if (!normalized) continue;
        expanded.add(normalized);

        for (const [canonical, aliases] of Object.entries(KEYWORD_ALIAS_MAP)) {
            if (canonical === normalized || aliases.some(alias => normalized.includes(alias) || alias.includes(normalized))) {
                expanded.add(canonical);
                for (const alias of aliases) {
                    expanded.add(alias);
                }
            }
        }
    }

    return [...expanded];
}

function inferImplicitTags(id: string, title: string, problemType: string, content: string): string[] {
    const source = `${id} ${title} ${problemType} ${content}`.toLowerCase();
    const inferred = new Set<string>();

    for (const [canonical, aliases] of Object.entries(KEYWORD_ALIAS_MAP)) {
        const candidates = [canonical, ...aliases];
        if (candidates.some(candidate => source.includes(candidate))) {
            inferred.add(canonical);
        }
    }

    if (source.includes('sketch') || source.includes('设计稿') || source.includes('还原')) {
        inferred.add('sketch');
    }

    return [...inferred];
}

function extractErrorKeywords(errorMessage?: string): string[] {
    if (!errorMessage?.trim()) {
        return [];
    }

    const rawMatches = errorMessage
        .toLowerCase()
        .match(/colorfilter|fill-opacity|viewbox|svgpicture(?:\.asset)?|image\.asset|bitmap|chevron|arrow|opacity|颜色|透明度|箭头|图标|位图|设计稿|还原|间距|布局|发灰|发浅|模糊|复用|旧资源|png|svg|icon/g) || [];

    return expandKeywords([...new Set(rawMatches)]);
}

/**
 * 从案例文件中提取元数据
 */
function extractCaseMetadata(filePath: string, framework: string): TroubleshootingCase | null {
    try {
        const content = fs.readFileSync(filePath, 'utf-8');
        const lines = content.split('\n');
        
        // 提取标题
        const titleLine = lines.find(l => l.trim().startsWith('# '));
        const title = titleLine ? titleLine.replace(/^#\s*/, '').trim() : path.basename(filePath, '.md');
        
        // 提取问题标签行
        let tags: string[] = [];
        let problemType = '';
        let severity = '中等';
        let timeSaved = '未知';
        
        for (let i = 0; i < Math.min(20, lines.length); i++) {
            const line = lines[i];
            
            // 提取标签
            if (line.includes('**问题标签**') || line.includes('**标签**')) {
                const tagsMatch = line.match(/[`"]([^`"]+)[`"]/g);
                if (tagsMatch) {
                    tags = tagsMatch.map(t => t.replace(/[`"]/g, '').trim());
                }
            }
            
            // 提取问题类型
            if (line.includes('**问题类型**')) {
                const typeMatch = line.match(/[`"]([^`"]+)[`"]/);
                if (typeMatch) {
                    problemType = typeMatch[1];
                }
            }
            
            // 提取严重程度
            if (line.includes('**严重程度**')) {
                const severityMatch = line.match(/低|中|高|中等/);
                if (severityMatch) {
                    severity = severityMatch[0];
                }
            }
        }
        
        // 从内容中查找节省时间信息
        const timeSavedMatch = content.match(/节省时间[：:]\s*([^\n]+)|(\d+-\d+)\s*轮/);
        if (timeSavedMatch) {
            timeSaved = timeSavedMatch[1] || `${timeSavedMatch[2]}轮对话`;
        }
        
        const id = path.basename(filePath, '.md');
        tags = [...new Set([...tags, ...inferImplicitTags(id, title, problemType || '', content)])];

        return {
            id,
            framework,
            title,
            problemType: problemType || '未分类',
            tags,
            severity,
            timeSaved,
            filePath,
        };
    } catch (error) {
        logger.error(`解析案例文件失败: ${filePath}`, error);
        return null;
    }
}

/**
 * 计算匹配分数
 * 
 * 评分维度：
 * 1. 关键词标签匹配 (0-40分): 关键词出现在标签中
 * 2. 关键词标题匹配 (0-30分): 关键词出现在标题中
 * 3. 关键词内容匹配 (0-15分): 关键词出现在问题类型/预览中
 * 4. 错误信息匹配 (0-30分): 错误信息与案例内容相似
 * 5. 标签精确匹配奖励 (+15分/个): 关键词与标签完全一致
 * 
 * 总分: 0-100+
 */
function calculateMatchScore(
    caseData: TroubleshootingCase,
    keywords: string[],
    errorMessage?: string
): number {
    let score = 0;
    const expandedKeywords = expandKeywords(keywords);
    
    const idLower = caseData.id.toLowerCase();
    const titleLower = caseData.title.toLowerCase();
    const problemTypeLower = caseData.problemType.toLowerCase();
    const tagsLower = caseData.tags.map(t => t.toLowerCase());
    const caseContent = fs.readFileSync(caseData.filePath, 'utf-8').toLowerCase();
    const searchableText = `${idLower} ${titleLower} ${problemTypeLower} ${tagsLower.join(' ')} ${caseContent}`;
    
    // 1. 标签匹配（最高权重 40分）
    for (const keyword of expandedKeywords) {
        const kw = keyword.toLowerCase();
        if (tagsLower.includes(kw)) {
            score += 20; // 标签精确匹配
        } else if (tagsLower.some(tag => tag.includes(kw) || kw.includes(tag))) {
            score += 10; // 标签部分匹配
        }
    }
    
    // 2. 标题匹配（30分）
    for (const keyword of expandedKeywords) {
        const kw = keyword.toLowerCase();
        if (titleLower.includes(kw) || idLower.includes(kw)) {
            score += 15;
        }
    }
    
    // 3. 问题类型匹配（15分）
    for (const keyword of expandedKeywords) {
        const kw = keyword.toLowerCase();
        if (problemTypeLower.includes(kw)) {
            score += 8;
        }
    }

    // 3.5 内容/文件名匹配（补足标签不足场景）
    for (const keyword of expandedKeywords) {
        const kw = keyword.toLowerCase();
        if (searchableText.includes(kw)) {
            score += 6;
        }
    }
    
    // 4. 错误信息匹配（30分）
    if (errorMessage && errorMessage.trim()) {
        const uniqueKeywords = extractErrorKeywords(errorMessage);
        
        let matches = 0;
        for (const kw of uniqueKeywords.slice(0, 10)) { // 最多检查10个关键词
            if (searchableText.includes(kw)) {
                matches++;
            }
        }
        
        // 按匹配比例给分
        if (uniqueKeywords.length > 0) {
            score += Math.floor((matches / uniqueKeywords.length) * 30);
        }
    }
    
    // 5. 精确标签匹配奖励（每个+15分）
    for (const keyword of expandedKeywords) {
        if (caseData.tags.map(tag => tag.toLowerCase()).includes(keyword.toLowerCase())) {
            score += 15;
        }
    }
    
    return Math.min(100, score);
}

/**
 * 生成案例预览
 * 优先提取最有价值的信息
 */
function generatePreview(filePath: string): string {
    try {
        const content = fs.readFileSync(filePath, 'utf-8');
        const lines = content.split('\n');
        
        // 优先级1: 查找"核心原理"章节
        let sectionStart = lines.findIndex(l => 
            l.includes('## ✅ 正确解决方案') ||
            l.includes('### 核心原理')
        );
        
        if (sectionStart !== -1) {
            const preview = lines
                .slice(sectionStart + 1, sectionStart + 8)
                .filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('>'))
                .join(' ')
                .replace(/```.*?```/g, '')
                .replace(/\*\*/g, '')
                .trim();
            
            if (preview.length > 50) {
                return preview.slice(0, 250) + '...';
            }
        }
        
        // 优先级2: 查找"问题原因"章节
        sectionStart = lines.findIndex(l =>
            l.includes('### 问题原因') ||
            l.includes('## ❌ 常见错误') ||
            l.includes('### 原因')
        );
        
        if (sectionStart !== -1) {
            const preview = lines
                .slice(sectionStart + 1, sectionStart + 8)
                .filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('|'))
                .join(' ')
                .replace(/```.*?```/g, '')
                .replace(/\*\*/g, '')
                .trim();
                
            if (preview.length > 50) {
                return preview.slice(0, 250) + '...';
            }
        }
        
        // 优先级3: 查找"问题识别"章节
        sectionStart = lines.findIndex(l => l.includes('## 🔍 问题识别'));
        
        if (sectionStart !== -1) {
            const preview = lines
                .slice(sectionStart + 1, sectionStart + 10)
                .filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('###'))
                .join(' ')
                .trim();
            
            if (preview.length > 50) {
                return preview.slice(0, 250) + '...';
            }
        }
        
        // 回退：返回前几行非空内容
        const fallbackPreview = lines
            .slice(0, 15)
            .filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('>') && !l.startsWith('**'))
            .slice(0, 3)
            .join(' ')
            .trim();
        
        return fallbackPreview.slice(0, 250) + '...';
    } catch (error) {
        return '预览不可用';
    }
}

/**
 * 查询故障排除案例
 */
export async function queryTroubleshootingCases(params: {
    framework?: string;
    keywords?: string[];
    errorMessage?: string;
    codePattern?: string;
    limit?: number;
}): Promise<TroubleshootingQueryResult> {
    const {
        framework,
        keywords = [],
        errorMessage,
        codePattern,
        limit = 5,
    } = params;
    
    logger.log('查询故障排除案例', { framework, keywords, errorMessage });
    
    try {
        const loader = new ResourceLoader();
        const troubleshootingDir = loader.getResourceDir('troubleshooting');
        
        if (!fs.existsSync(troubleshootingDir)) {
            logger.warn('故障排除目录不存在:', troubleshootingDir);
            return {
                cases: [],
                totalFound: 0,
                queryInfo: { framework, keywords, errorMessage },
            };
        }
        
        // 收集所有案例
        const allCases: TroubleshootingCase[] = [];
        
        // 确定要搜索的框架目录
        const frameworksToSearch: string[] = [];
        if (framework) {
            frameworksToSearch.push(framework);
        } else {
            // 搜索所有框架目录
            const dirs = fs.readdirSync(troubleshootingDir);
            for (const dir of dirs) {
                const dirPath = path.join(troubleshootingDir, dir);
                if (fs.statSync(dirPath).isDirectory()) {
                    frameworksToSearch.push(dir);
                }
            }
        }
        
        // 遍历框架目录收集案例
        for (const fw of frameworksToSearch) {
            const fwDir = path.join(troubleshootingDir, fw);
            if (!fs.existsSync(fwDir)) continue;
            
            const files = fs.readdirSync(fwDir);
            for (const file of files) {
                if (!file.endsWith('.md') || file === 'README.md') continue;
                
                const filePath = path.join(fwDir, file);
                const metadata = extractCaseMetadata(filePath, fw);
                if (metadata) {
                    allCases.push(metadata);
                }
            }
        }
        
        logger.log(`找到 ${allCases.length} 个案例`);
        
        // 如果有关键词或错误信息，进行匹配评分
        let scoredCases = allCases.map(caseData => ({
            caseData,
            score: calculateMatchScore(caseData, keywords, errorMessage || codePattern),
        }));
        
        // 按分数排序
        scoredCases.sort((a, b) => b.score - a.score);
        
        // 智能过滤阈值
        let threshold = 10; // 基础阈值
        
        // 如果有错误信息，降低阈值（更宽松）
        if (errorMessage && errorMessage.trim()) {
            threshold = 5;
        }
        
        // 如果关键词很多，提高阈值（更严格）
        if (keywords.length >= 3) {
            threshold = 15;
        }
        
        scoredCases = scoredCases.filter(c => c.score >= threshold);
        
        // 限制返回数量
        const topCases = scoredCases.slice(0, limit);
        
        // 生成结果
        const results = topCases.map(({ caseData, score }) => ({
            id: caseData.id,
            title: caseData.title,
            framework: caseData.framework,
            problemType: caseData.problemType,
            tags: caseData.tags,
            matchScore: score,
            timeSaved: caseData.timeSaved,
            preview: generatePreview(caseData.filePath),
        }));
        
        return {
            cases: results,
            totalFound: scoredCases.length,
            queryInfo: { framework, keywords, errorMessage },
        };
    } catch (error) {
        logger.error('查询故障排除案例失败', error);
        throw error;
    }
}

/**
 * 获取案例完整内容
 */
export async function getTroubleshootingCaseContent(params: {
    framework: string;
    caseId: string;
}): Promise<{ content: string; metadata: TroubleshootingCase | null }> {
    const { framework, caseId } = params;
    
    try {
        const loader = new ResourceLoader();
        const troubleshootingDir = loader.getResourceDir('troubleshooting');
        const casePath = path.join(troubleshootingDir, framework, `${caseId}.md`);
        
        if (!fs.existsSync(casePath)) {
            throw new Error(`案例不存在: ${framework}/${caseId}`);
        }
        
        const content = fs.readFileSync(casePath, 'utf-8');
        const metadata = extractCaseMetadata(casePath, framework);
        
        return { content, metadata };
    } catch (error) {
        logger.error('获取案例内容失败', error);
        throw error;
    }
}

/**
 * 列出所有可用案例
 */
export async function listTroubleshootingCases(framework?: string): Promise<{
    cases: TroubleshootingCase[];
    frameworks: string[];
}> {
    try {
        const loader = new ResourceLoader();
        const troubleshootingDir = loader.getResourceDir('troubleshooting');
        
        const frameworks: string[] = [];
        const allCases: TroubleshootingCase[] = [];
        
        if (!fs.existsSync(troubleshootingDir)) {
            return { cases: [], frameworks: [] };
        }
        
        const dirs = fs.readdirSync(troubleshootingDir);
        for (const dir of dirs) {
            const dirPath = path.join(troubleshootingDir, dir);
            if (!fs.statSync(dirPath).isDirectory()) continue;
            if (framework && dir !== framework) continue;
            
            frameworks.push(dir);
            
            const files = fs.readdirSync(dirPath);
            for (const file of files) {
                if (!file.endsWith('.md') || file === 'README.md') continue;
                
                const filePath = path.join(dirPath, file);
                const metadata = extractCaseMetadata(filePath, dir);
                if (metadata) {
                    allCases.push(metadata);
                }
            }
        }
        
        return {
            cases: allCases,
            frameworks,
        };
    } catch (error) {
        logger.error('列出故障排除案例失败', error);
        throw error;
    }
}
